import React, { useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { SectionFieldDTO, FieldEntryDTO } from '../magic-report.type';
import { TestData, parseTestData, serializeTestData } from './MaterialTestingTable';

interface MaterialTestingFailedTestsTableProps {
  testsField: SectionFieldDTO | undefined;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
}

interface FailedTestRow {
  testIndex: number;
  segmentIndex: number;
  testName: string;
  segmentText: string;
}

type EditingCell = { testIndex: number; segmentIndex: number; field: 'testName' | 'segment' };

export const splitFailedTestSegments = (failedTests: string): string[] =>
  (failedTests || '')
    .split(';')
    .map(segment => segment.trim())
    .filter(segment => segment.length > 0);

const CELL_SX = {
  px: 1,
  py: 0.75,
  borderBottom: '1px solid',
  borderColor: 'grey.200',
};

export const MaterialTestingFailedTestsTable: React.FC<MaterialTestingFailedTestsTableProps> = ({ testsField, handleFieldChange }) => {
  const tests: TestData[] = (testsField?.entries || [])
    .map(entry => parseTestData(entry.value || ''))
    .filter((test): test is TestData => test !== null);

  const rows: FailedTestRow[] = [];
  tests.forEach((test, testIndex) => {
    splitFailedTestSegments(test.failedTests).forEach((segmentText, segmentIndex) => {
      rows.push({ testIndex, segmentIndex, testName: test.testName, segmentText });
    });
  });

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [draft, setDraft] = useState('');

  if (rows.length === 0) return null;

  const saveTests = (updatedTests: TestData[]) => {
    const entries: FieldEntryDTO[] = updatedTests.map((test, i) => ({
      value: serializeTestData(test),
      addToActionItems: false,
      order: i,
    }));
    handleFieldChange('MaterialTestingTests', { entries });
  };

  const startEdit = (row: FailedTestRow, field: EditingCell['field']) => {
    setEditingCell({ testIndex: row.testIndex, segmentIndex: row.segmentIndex, field });
    setDraft(field === 'testName' ? row.testName : row.segmentText);
  };

  const commitEdit = () => {
    if (!editingCell) return;
    const { testIndex, segmentIndex, field } = editingCell;
    const updatedTests = [...tests];
    const target = updatedTests[testIndex];

    if (field === 'testName') {
      updatedTests[testIndex] = { ...target, testName: draft };
    } else {
      const segments = splitFailedTestSegments(target.failedTests);
      segments[segmentIndex] = draft;
      updatedTests[testIndex] = { ...target, failedTests: segments.join('; ') };
    }

    saveTests(updatedTests);
    setEditingCell(null);
  };

  const cancelEdit = () => setEditingCell(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
        <Typography variant='body2' fontWeight='medium' sx={{ ...CELL_SX, borderBottom: '2px solid', borderColor: 'grey.400' }}>
          Test Type
        </Typography>
        <Typography variant='body2' fontWeight='medium' sx={{ ...CELL_SX, borderBottom: '2px solid', borderColor: 'grey.400' }}>
          Failed Test No. / Information
        </Typography>
      </Box>

      {rows.map(row => {
        const rowKey = `${row.testIndex}-${row.segmentIndex}`;
        return (
          <Box key={rowKey} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
            {(['testName', 'segment'] as EditingCell['field'][]).map(field => {
              const isEditingThis =
                editingCell?.testIndex === row.testIndex && editingCell.segmentIndex === row.segmentIndex && editingCell.field === field;
              const displayText = field === 'testName' ? row.testName : row.segmentText;

              return (
                <Box
                  key={field}
                  onDoubleClick={() => startEdit(row, field)}
                  sx={{ ...CELL_SX, cursor: 'text', '&:hover': { bgcolor: 'action.hover' } }}
                >
                  {isEditingThis ? (
                    <TextField
                      size='small'
                      fullWidth
                      multiline
                      autoFocus
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          commitEdit();
                        } else if (e.key === 'Escape') {
                          cancelEdit();
                        }
                      }}
                    />
                  ) : (
                    <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
                      {displayText}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};
